import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Truck, Package, DollarSign, Clock, Search, Star, TrendingUp, BarChart3, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const T = {
  bg: '#09090b', paper: '#18181b', subtle: '#27272a',
  primary: '#3b82f6', accent: '#f97316',
  success: '#22c55e', warning: '#eab308', danger: '#ef4444', info: '#0ea5e9',
  border: '#27272a', text: '#fafafa', textMuted: '#a1a1aa', textDim: '#71717a',
};

const QUALITY_COLORS = { Premium: '#22c55e', Standard: '#3b82f6', Economy: '#f97316' };

const SupplierComparisonView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('value');
  const [expanded, setExpanded] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/wms/supplier-comparison?months=${months}`);
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = (data?.suppliers || [])
    .filter(s => !search || s.supplier_name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'value') return b.total_receipts_value - a.total_receipts_value;
      if (sortBy === 'articles') return b.articles_count - a.articles_count;
      if (sortBy === 'price') return b.avg_price - a.avg_price;
      if (sortBy === 'lead_time') return a.avg_lead_time_days - b.avg_lead_time_days;
      return 0;
    });

  // Top 10 for charts
  const top10 = filtered.slice(0, 10);
  const chartData = top10.map(s => ({
    name: s.supplier_name?.length > 15 ? s.supplier_name.substring(0, 15) + '...' : s.supplier_name,
    value: s.total_receipts_value,
    qty: s.total_receipts_qty,
    articles: s.articles_count,
  }));

  // Quality pie data
  const qualityTotals = { Premium: 0, Standard: 0, Economy: 0 };
  (data?.suppliers || []).forEach(s => {
    qualityTotals.Premium += s.quality_distribution?.Premium || 0;
    qualityTotals.Standard += s.quality_distribution?.Standard || 0;
    qualityTotals.Economy += s.quality_distribution?.Economy || 0;
  });
  const pieData = Object.entries(qualityTotals).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="supplier-comparison-view">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.textDim }} />
          <Input placeholder="Cauta furnizor..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="supplier-search" />
        </div>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="w-36 h-8 text-xs bg-zinc-800 border-zinc-700 text-zinc-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Ultimele 3 luni</SelectItem>
            <SelectItem value="6">Ultimele 6 luni</SelectItem>
            <SelectItem value="12">Ultimele 12 luni</SelectItem>
            <SelectItem value="24">Ultimele 24 luni</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40 h-8 text-xs bg-zinc-800 border-zinc-700 text-zinc-300" data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="value">Valoare receptii</SelectItem>
            <SelectItem value="articles">Nr. articole</SelectItem>
            <SelectItem value="price">Pret mediu</SelectItem>
            <SelectItem value="lead_time">Termen livrare</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard icon={Truck} label="Furnizori activi" value={data?.summary?.total_suppliers || 0} color={T.primary} />
        <SummaryCard icon={DollarSign} label="Valoare totala" value={`${((data?.summary?.total_value || 0) / 1000).toFixed(1)}k`} color={T.success} sub="RON" />
        <SummaryCard icon={Package} label="Articole mapate" value={data?.summary?.total_articles_mapped || 0} color={T.accent} />
        <SummaryCard icon={Clock} label="Perioada" value={`${data?.summary?.period_months || 6} luni`} color={T.info} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Bar chart - top suppliers by value */}
        <div className="col-span-2 rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
          <h3 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: T.textMuted }}>
            <BarChart3 className="w-4 h-4 text-blue-400" /> Top 10 Furnizori (articole mapate)
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 9 }} axisLine={false} tickLine={false} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fill: T.textDim, fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: T.paper, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 11 }} />
              <Bar dataKey="articles" fill={T.primary} radius={[4, 4, 0, 0]} name="Articole" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Quality distribution pie */}
        <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
          <h3 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: T.textMuted }}>
            <Award className="w-4 h-4 text-emerald-400" /> Distributie Calitate
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {pieData.map((entry, i) => <Cell key={i} fill={QUALITY_COLORS[entry.name] || T.textDim} />)}
                </Pie>
                <Tooltip contentStyle={{ background: T.paper, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[160px]" style={{ color: T.textDim }}>
              <p className="text-xs">Nicio distributie</p>
            </div>
          )}
          <div className="flex justify-center gap-3 mt-2">
            {Object.entries(QUALITY_COLORS).map(([k, c]) => (
              <div key={k} className="flex items-center gap-1.5 text-[10px]" style={{ color: T.textMuted }}>
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
                {k}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Supplier cards */}
      <div className="space-y-2" data-testid="supplier-cards">
        {filtered.length === 0 ? (
          <div className="text-center py-12 rounded-xl border" style={{ background: T.paper, borderColor: T.border }}>
            <Truck className="w-10 h-10 mx-auto mb-2 opacity-20" style={{ color: T.textDim }} />
            <p className="text-sm" style={{ color: T.textMuted }}>Niciun furnizor gasit</p>
          </div>
        ) : filtered.map((s, i) => (
          <div key={s.supplier_id} className="rounded-xl border overflow-hidden transition-all hover:border-blue-500/30"
            style={{ background: T.paper, borderColor: expanded === s.supplier_id ? `${T.primary}60` : T.border }}
            data-testid={`supplier-card-${i}`}>
            {/* Main row */}
            <div className="flex items-center gap-4 px-4 py-3 cursor-pointer" onClick={() => setExpanded(expanded === s.supplier_id ? null : s.supplier_id)}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 font-bold text-sm"
                style={{ background: `${T.primary}15`, color: T.primary }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{s.supplier_name}</p>
                <p className="text-[10px]" style={{ color: T.textDim }}>{s.articles_count} articole mapate</p>
              </div>
              <div className="flex items-center gap-6 shrink-0">
                <MetricCell icon={DollarSign} label="Pret mediu" value={s.avg_price > 0 ? `${s.avg_price} LEI` : '-'} color={T.success} />
                <MetricCell icon={Clock} label="Termen" value={s.avg_lead_time_days > 0 ? `${s.avg_lead_time_days} zile` : '-'} color={T.warning} />
                <MetricCell icon={TrendingUp} label="Receptii" value={s.total_receipts_qty || '-'} color={T.info} />
                <div className="flex gap-1">
                  {Object.entries(s.quality_distribution || {}).map(([grade, count]) => count > 0 && (
                    <span key={grade} className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${QUALITY_COLORS[grade]}20`, color: QUALITY_COLORS[grade] }}>
                      {grade[0]}: {count}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Expanded details */}
            {expanded === s.supplier_id && (
              <div className="px-4 pb-4 pt-1 border-t space-y-3" style={{ borderColor: T.border }}>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg p-3" style={{ background: T.subtle }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: T.textDim }}>Valoare Receptii</p>
                    <p className="text-lg font-bold" style={{ color: T.success }}>{s.total_receipts_value.toLocaleString('ro-RO')} LEI</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: T.subtle }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: T.textDim }}>Cantitate Receptii</p>
                    <p className="text-lg font-bold" style={{ color: T.info }}>{s.total_receipts_qty}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: T.subtle }}>
                    <p className="text-[10px] font-medium mb-1" style={{ color: T.textDim }}>Cantitate Consumata</p>
                    <p className="text-lg font-bold" style={{ color: T.danger }}>{s.total_consumed_qty}</p>
                  </div>
                </div>

                {/* Monthly trend mini chart */}
                {s.monthly_trend?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold mb-1" style={{ color: T.textDim }}>Trend lunar</p>
                    <ResponsiveContainer width="100%" height={80}>
                      <BarChart data={s.monthly_trend} barGap={1}>
                        <XAxis dataKey="month" tick={{ fill: T.textDim, fontSize: 9 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: T.paper, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 10 }} />
                        <Bar dataKey="receipts" fill={T.success} radius={[2, 2, 0, 0]} name="Receptii" />
                        <Bar dataKey="consumption" fill={T.danger} radius={[2, 2, 0, 0]} name="Consum" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Top articles */}
                {s.top_articles?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold mb-1" style={{ color: T.textDim }}>Top articole</p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.top_articles.map((a, j) => (
                        <span key={j} className="px-2 py-0.5 rounded text-[10px]" style={{ background: T.subtle, color: T.textMuted }}>
                          {a.code || a.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="rounded-xl border p-4 transition-all hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
    style={{ background: T.paper, borderColor: T.border }}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-extrabold font-mono" style={{ color }}>{value}</p>
        <p className="text-[11px]" style={{ color: T.textDim }}>{label}</p>
        {sub && <p className="text-[10px]" style={{ color: T.textDim }}>{sub}</p>}
      </div>
    </div>
  </div>
);

const MetricCell = ({ icon: Icon, label, value, color }) => (
  <div className="text-center">
    <p className="text-xs font-bold font-mono" style={{ color }}>{value}</p>
    <p className="text-[9px]" style={{ color: T.textDim }}>{label}</p>
  </div>
);

export default SupplierComparisonView;
